import { useMutation, useQueryClient } from "@tanstack/react-query";

import { toast } from "@/shared/hooks/use-toast";

import { templatesApi } from "../api";
import type { TemplateRecord } from "../api";
import { templateKeys } from "./template.keys";

interface AddCategoryVars {
  templateId: string;
  categoryId: string;
}

interface RemoveCategoryVars {
  templateId: string;
  categoryId: string;
}

interface ApiError extends Error {
  response?: { status?: number };
  status?: number;
}

function extractStatus(err: unknown): number | undefined {
  if (!err || typeof err !== "object") return undefined;
  const e = err as ApiError;
  return e.response?.status ?? e.status;
}

export function useAddTemplateCategory() {
  const queryClient = useQueryClient();
  return useMutation<TemplateRecord, Error, AddCategoryVars>({
    mutationFn: ({ templateId, categoryId }) =>
      templatesApi.addCategory(templateId, { categoryId }),
    onSuccess: (data) => {
      queryClient.setQueryData(templateKeys.detail(data.id), data);
      queryClient.invalidateQueries({ queryKey: templateKeys.lists() });
    },
    onError: (err) => {
      const status = extractStatus(err);
      if (status === 409) {
        toast({
          title: "Categoria já associada",
          description: "Este template já está nessa categoria.",
        });
        return;
      }
      toast({
        title: "Erro ao adicionar categoria",
        description: err.message,
        variant: "destructive",
      });
    },
  });
}

export function useRemoveTemplateCategory() {
  const queryClient = useQueryClient();
  return useMutation<void, Error, RemoveCategoryVars>({
    mutationFn: ({ templateId, categoryId }) =>
      templatesApi.removeCategory(templateId, categoryId),
    onSuccess: (_data, vars) => {
      queryClient.invalidateQueries({ queryKey: templateKeys.detail(vars.templateId) });
      queryClient.invalidateQueries({ queryKey: templateKeys.lists() });
    },
    onError: (err) => {
      toast({
        title: "Erro ao remover categoria",
        description: err.message,
        variant: "destructive",
      });
    },
  });
}
